import {
  useTheme,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Button,
  Grid,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridApi,
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useState } from 'react';
import type { GridStateColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { DatabaseTableSkeleton } from './DatabaseTableSkeleton';

/**
 * DatabaseTable Component
 * 
 * Permite sobrescrever as actions padrão usando a prop `renderCustomActions`:
 * 
 * @example
 * ```tsx
 * <DatabaseTable
 *   rows={data}
 *   columns={columns}
 *   renderCustomActions={(row) => (
 *     <div style={{ display: 'flex', gap: 8 }}>
 *       <IconButton onClick={() => handleCustomAction(row)}>
 *         <CustomIcon />
 *       </IconButton>
 *       <IconButton onClick={() => handleAnotherAction(row)}>
 *         <AnotherIcon />
 *       </IconButton>
 *     </div>
 *   )}
 * />
 * ```
 */

interface BaseRow {
  id: string | number;
  [key: string]: unknown;
}

interface DatabaseTableProps<T extends BaseRow> {
  title?: string;
  subtitle?: string;
  rows: T[];
  columns: GridColDef[];
  pageSizeOptions?: number[];
  sx?: object;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onView?: (row: T) => void;
  onPdf?: (row: T) => void;
  hideEdit?: boolean;
  hideDelete?: boolean;
  hideView?: boolean;
  hidePdf?: boolean;
  loading?: boolean;
  checkboxSelection?: boolean;
  groupBy?: string;
  filterFields?: React.ReactNode;
  onFilter?: () => void;
  onClearFilter?: () => void;
  defaultFilterOpen?: boolean;
  renderCustomActions?: (row: T) => React.ReactNode;
}

export function DatabaseTable<T extends BaseRow>({
  title,
  subtitle,
  rows,
  columns,
  pageSizeOptions = [5, 10],
  sx = {},
  onEdit,
  onDelete,
  onView,
  onPdf,
  hideEdit = false,
  hideDelete = false,
  hideView = false,
  hidePdf = false,
  loading = false,
  checkboxSelection = false,
  groupBy,
  filterFields,
  onFilter,
  onClearFilter,
  defaultFilterOpen = false,
  renderCustomActions,
}: DatabaseTableProps<T>) {
  const theme = useTheme();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [filterOpen, setFilterOpen] = useState(defaultFilterOpen);

  const showActions = !hideEdit || !hideDelete || !hideView || !!onPdf || !!renderCustomActions;

  if (loading) {
    return (
      <DatabaseTableSkeleton
        columns={columns.length}
        rows={5}
        showActions={showActions}
        title={title}
        subtitle={subtitle}
      />
    );
  }

  const renderActions = (row: T) => {
    if (renderCustomActions) {
      return renderCustomActions(row);
    }
    return (
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', width: '100%' }}>
        {!hideView && (
          <Tooltip title="Visualizar">
            <IconButton size="small" color="primary" onClick={() => onView && onView(row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!hidePdf && onPdf && (
          <Tooltip title="Exportar PDF">
            <IconButton size="small" sx={{ color: '#6C63FF' }} onClick={() => onPdf(row)}>
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!hideEdit && (
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => onEdit && onEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!hideDelete && (
          <Tooltip title="Remover">
            <IconButton size="small" color="error" onClick={() => onDelete && onDelete(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderFilter = filterFields && (
    <Box mb={3}>
      <Button
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={() => setFilterOpen(prev => !prev)}
        sx={{
          mb: 1,
          fontWeight: 700,
          color: '#6C63FF',
          borderColor: '#6C63FF',
          '&:hover': { borderColor: '#6C63FF', background: 'rgba(108,99,255,0.04)' },
        }}
      >
        {filterOpen ? 'Ocultar Filtros' : 'Mostrar Filtros'}
      </Button>
      <Collapse in={filterOpen}>
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            background: theme.palette.background.paper,
            borderRadius: 3,
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>{filterFields}</Box>
            </Grid>
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                {onFilter && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onFilter}
                    sx={{ minWidth: 110 }}
                  >
                    Filtrar
                  </Button>
                )}
                {onClearFilter && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onClearFilter}
                    sx={{ minWidth: 110 }}
                  >
                    Limpar
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );

  if (!groupBy) {
    const columnsWithActions = showActions
      ? [
          ...columns,
          {
            field: 'actions',
            headerName: '',
            width: 120,
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<T>) => renderActions(params.row),
          } as GridColDef,
        ]
      : columns;

    return (
      <div
        style={{
          background: theme.palette.background.paper,
          borderRadius: 16,
          padding: 32,
          boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
          maxWidth: '100%',
          margin: '0 auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <div>
            <h2
              style={{
                color: theme.palette.text.primary,
                fontWeight: 800,
                fontSize: 28,
                margin: 0,
                letterSpacing: -1,
              }}
            >
              {title}
            </h2>
            {subtitle && (
              <p style={{ color: '#6B7280', fontSize: 15, margin: 0, marginTop: 4 }}>{subtitle}</p>
            )}
          </div>
        </div>
        {renderFilter}
        <div
          style={{
            width: '100%',
            background: theme.palette.background.paper,
            borderRadius: 12,
            minHeight: 180,
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight={180}>
              <CircularProgress />
            </Box>
          ) : !rows || rows.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={220}
              sx={{
                opacity: 0.7,
                color: theme.palette.text.secondary,
                gap: 1,
                py: 4,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background:
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <VisibilityIcon sx={{ fontSize: 32, color: 'inherit', opacity: 0.5 }} />
              </Box>
              <Typography color="text.secondary" fontWeight={600} fontSize={18}>
                Nenhum dado encontrado
              </Typography>
              <Typography color="text.secondary" fontSize={15}>
                Não há registros para exibir nesta tabela.
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={rows}
              columns={columnsWithActions}
              autoHeight
              disableColumnMenu
              hideFooterSelectedRowCount
              checkboxSelection={checkboxSelection}
              pageSizeOptions={pageSizeOptions}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderColor: theme.palette.divider,
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor:
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-columnHeader': {
                  borderColor: theme.palette.divider,
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                },
                ...sx,
              }}
            />
          )}
        </div>
      </div>
    );
  }

  const groupedRows = rows.reduce((acc: Record<string, T[]>, row: T) => {
    const groupKey = row[groupBy];
    if (!acc[groupKey as string]) {
      acc[groupKey as string] = [];
    }
    acc[groupKey as string].push(row);
    return acc;
  }, {});

  const visibleColumns = columns.filter(col => col.field !== groupBy);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  return (
    <div
      style={{
        background: theme.palette.background.paper,
        borderRadius: 16,
        padding: 32,
        boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
        maxWidth: '100%',
        margin: '0 auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <div>
          <h2
            style={{
              color: theme.palette.text.primary,
              fontWeight: 800,
              fontSize: 28,
              margin: 0,
              letterSpacing: -1,
            }}
          >
            {title}
          </h2>
          {subtitle && (
            <p style={{ color: '#6B7280', fontSize: 15, margin: 0, marginTop: 4 }}>{subtitle}</p>
          )}
        </div>
      </div>
      <TableContainer component={Paper} sx={{ boxShadow: 'none', background: 'transparent' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} align="center" />
              {visibleColumns.map(column => (
                <TableCell
                  key={column.field}
                  align="center"
                  style={{
                    color: '#6B7280',
                    fontWeight: 700,
                    fontSize: 15,
                    border: 'none',
                  }}
                >
                  {column.headerName}
                </TableCell>
              ))}
              {showActions && <TableCell align="center" style={{ border: 'none' }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(groupedRows).map(([groupKey, groupRows]) => (
              <>
                <TableRow
                  key={groupKey}
                  sx={{
                    '& > *': { borderBottom: 'unset' },
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleGroup(groupKey)}>
                      {expandedGroups[groupKey] ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell colSpan={columns.length + (showActions ? 1 : 0)}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {groupKey}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={columns.length + (showActions ? 2 : 1)}
                  >
                    <Collapse in={expandedGroups[groupKey]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Table size="small">
                          <TableBody>
                            {groupRows.map((row, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  '&:last-child td, &:last-child th': { border: 0 },
                                  '&:hover': {
                                    backgroundColor:
                                      theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.05)'
                                        : 'rgba(0, 0, 0, 0.02)',
                                  },
                                }}
                              >
                                <TableCell width={50} align="center" />
                                {visibleColumns.map(column => (
                                  <TableCell
                                    key={column.field}
                                    align="center"
                                    style={{ border: 'none' }}
                                  >
                                    {column.renderCell
                                      ? column.renderCell({
                                          value: row[column.field],
                                          row,
                                          field: column.field,
                                          id: row.id ?? index,
                                          colDef: column as GridStateColDef,
                                          rowNode: {
                                            id: row.id ?? index,
                                            isAutoGenerated: false,
                                            type: 'group',
                                            parent: null,
                                            depth: 0,
                                            groupingKey: '',
                                            groupingField: '',
                                            children: [],
                                            childrenFromPath: {},
                                          },
                                          api: {} as unknown as GridApi,
                                          cellMode: 'view',
                                          hasFocus: false,
                                          tabIndex: -1,
                                        })
                                      : String(row[column.field])}
                                  </TableCell>
                                ))}
                                {showActions && (
                                  <TableCell align="center" style={{ border: 'none' }}>
                                    {renderActions(row)}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}